import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ClientResultContest,
  ClientResultDetailResponse,
  downloadClientResultPdf,
  getClientResultDetail,
} from "@/Api/clientResults";
import { useAuth } from "@/hooks/auth";
import { Mask } from "@/utils/mask";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import {
  ClientResultsFilterState,
  FiltersBar,
  LEGAL_NOTICE_TITLE,
  defaultFilters,
  filtersFromSearch,
  filtersToSearch,
  formatDateBr,
  lotteryColor,
  toParams,
} from "./shared";

const titleCase = (s: string) =>
  s.toLowerCase().replace(/(^|\s|-)(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());

const slug = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const ContestBlock = ({ contest }: { contest: ClientResultContest }) => {
  const investigated = contest.status === "investigated";
  const color = lotteryColor(contest.lottery_color);

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex items-center justify-between px-4 py-2 text-white" style={{ backgroundColor: color }}>
        <div>
          <p className="font-semibold">
            {contest.lottery_name.toLowerCase()} | Concurso {contest.contest_name}
          </p>
          <p className="text-xs opacity-90">Data de validade: {formatDateBr(contest.validation_date)}</p>
        </div>
        <span className="rounded bg-white/20 px-2 py-0.5 text-xs font-medium">
          {investigated ? "Apuração realizada" : "Não apurado"}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="px-4 py-2 text-left font-normal">Bolão</th>
              <th className="px-4 py-2 text-right font-normal">Prêmio por cota</th>
              <th className="px-4 py-2 text-center font-normal">Cotas do cliente</th>
              <th className="px-4 py-2 text-right font-normal">Prêmio do cliente</th>
            </tr>
          </thead>
          <tbody>
            {(contest.products ?? []).map((p) => (
              <tr key={p.contest_product_id} className="border-b last:border-0">
                <td className="px-4 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">Bolão {p.product_name}</span>
                    {p.receive_by_cashier && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                        Recebido no caixa
                      </span>
                    )}
                    {investigated && p.investigation_status === "not_awarded" && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        Não premiado
                      </span>
                    )}
                  </div>
                  {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {p.prize_per_quota === null ? "-" : Mask.formatCurrency(p.prize_per_quota)}
                </td>
                <td className="px-4 py-2 text-center tabular-nums">{p.quotas}</td>
                <td
                  className={cn(
                    "px-4 py-2 text-right tabular-nums",
                    (p.prize ?? 0) > 0 &&
                      (p.receive_by_cashier
                        ? "font-semibold text-blue-700 dark:text-blue-400"
                        : "font-semibold text-green-700 dark:text-green-400")
                  )}
                >
                  {p.prize === null ? "-" : Mask.formatCurrency(p.prize)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {investigated ? (
        <div className="divide-y">
          <div className="flex items-center justify-between bg-green-50 px-4 py-2 font-semibold dark:bg-green-900/10">
            <span className="text-sm">Total prêmio recebe na lotérica (e entra saldo)</span>
            <span className="tabular-nums text-green-700 dark:text-green-400">
              {Mask.formatCurrency(contest.total_wallet_prize ?? 0)}
            </span>
          </div>
          <div className="flex items-center justify-between bg-blue-50 px-4 py-2 font-semibold dark:bg-blue-900/10">
            <span className="text-sm">Total pagamento só na Caixa</span>
            <span className="tabular-nums text-blue-700 dark:text-blue-400">
              {Mask.formatCurrency(contest.total_cashier_prize ?? 0)}
            </span>
          </div>
          <div className="flex items-center justify-between bg-zinc-100 px-4 py-2 font-bold text-blue-900 dark:bg-zinc-800 dark:text-blue-300">
            <span className="text-sm">Prêmio Total (não entra pago Caixa na carteira)</span>
            <span className="tabular-nums">{Mask.formatCurrency(contest.total_prize ?? 0)}</span>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/10 dark:text-red-300">
          Concurso ainda não apurado. O resultado aparece quando todos os bolões forem apurados.
        </div>
      )}
    </div>
  );
};

/**
 * Apuração do cliente: demonstrativo por concurso para enviar ao cliente (Baixar PDF).
 */
export const PageDetail = () => {
  const { uuid = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const isSeller = !!auth?.user?.seller?.id;

  const [filters, setFilters] = useState<ClientResultsFilterState>(() => filtersFromSearch(searchParams));
  const [detail, setDetail] = useState<ClientResultDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchData = async (f: ClientResultsFilterState) => {
    if (f.date_start && f.date_end && f.date_start > f.date_end) {
      toast.error("A data final deve ser igual ou posterior à data inicial.");
      return;
    }
    setLoading(true);
    try {
      const { data, ok } = await getClientResultDetail(uuid, { ...toParams(f), search: undefined });
      if (ok && data?.response) {
        setDetail(data.response);
      } else {
        setDetail(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const apply = (f: ClientResultsFilterState) => {
    setSearchParams(new URLSearchParams(filtersToSearch(f)), { replace: true });
    fetchData(f);
  };

  const handleClear = () => {
    const f = { ...defaultFilters(), seller_id: filters.seller_id, unity_id: filters.unity_id };
    setFilters(f);
    apply(f);
  };

  const handleDownload = async () => {
    if (!detail) return;
    setDownloading(true);
    try {
      const blob = await downloadClientResultPdf(uuid, { ...toParams(filters), search: undefined });
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `resultado-${slug(detail.client.name || "cliente")}-${formatDateBr(
        detail.filters.date_start
      ).replace(/\//g, "-")}-a-${formatDateBr(detail.filters.date_end).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado!");
    } catch (error) {
      console.error("Erro ao baixar o PDF do resultado do cliente:", error);
      toast.error("Erro ao gerar o PDF");
    } finally {
      setDownloading(false);
    }
  };

  const backToList = () => {
    navigate(`/resultado-clientes?${filtersToSearch({ ...filters, search: "" })}`);
  };

  const client = detail?.client;
  const investigated = (detail?.contests ?? []).filter((c) => c.status === "investigated");
  const headline = investigated.map((c) => `${titleCase(c.lottery_name)} ${c.contest_name}`).join(" + ");

  return (
    <Main>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={backToList} title="Voltar para a lista">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-semibold leading-tight">Apuração do cliente</h2>
            <p className="text-sm text-muted-foreground">Vendas / Resultado Clientes</p>
          </div>
        </div>
        <Button className={colors.bg_primary} onClick={handleDownload} disabled={!detail || loading || downloading}>
          {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          Baixar PDF
        </Button>
      </div>

      {loading && !detail ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !detail || !client ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">Cliente não encontrado.</CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="grid grid-cols-1 gap-4 p-5 md:grid-cols-3">
              <div className="md:col-span-2">
                <p className="text-xs uppercase text-muted-foreground">Nome do cadastro</p>
                <p className="text-xl font-bold uppercase text-emerald-800 dark:text-emerald-400">{client.name}</p>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span>
                    <span className="text-xs uppercase text-muted-foreground">CPF: </span>
                    {client.document ? Mask.maskCpfOrCnpj(client.document) : "-"}
                  </span>
                  <span>
                    <span className="text-xs uppercase text-muted-foreground">Telefone: </span>
                    {client.phone ? Mask.maskPhone(client.phone) : "-"}
                  </span>
                  <span>
                    <span className="text-xs uppercase text-muted-foreground">Saldo atual: </span>
                    <strong className="tabular-nums">{Mask.formatCurrency(client.total_balance)}</strong>
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 p-4 dark:bg-emerald-900/20">
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Total em prêmios concursos</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                  {Mask.formatCurrency(detail.summary.total_prize)}
                </p>
                {headline && <p className="text-xs text-muted-foreground">{headline}</p>}
              </div>
            </CardContent>
          </Card>

          <FiltersBar
            value={filters}
            onChange={setFilters}
            onApply={() => apply(filters)}
            onClear={handleClear}
            showSellerFilters={!isSeller}
            showSearch={false}
            loading={loading}
          />

          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Resultado por concurso</h3>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>

          {detail.contests.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                Nenhuma compra deste cliente nos concursos do filtro.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {detail.contests.map((contest) => (
                <ContestBlock key={contest.contest_id} contest={contest} />
              ))}
            </div>
          )}

          {detail.contests.length > 0 && (
            <Card>
              <CardContent className="space-y-2 p-5">
                <h3 className="text-lg font-semibold">Resumo geral da apuração</h3>
                {detail.contests.map((c) => (
                  <div key={c.contest_id} className="flex justify-between border-b py-1 text-sm last:border-0">
                    <span className="flex items-center gap-2">
                      <span
                        className="size-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: lotteryColor(c.lottery_color) }}
                      />
                      {titleCase(c.lottery_name)} - Concurso {c.contest_name}
                    </span>
                    <span className={cn("tabular-nums", c.status !== "investigated" && "text-red-600")}>
                      {c.status === "investigated" ? Mask.formatCurrency(c.total_prize ?? 0) : "Não apurado"}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between border-b py-1 text-sm">
                  <span>Total prêmio recebe na lotérica (e entra saldo)</span>
                  <span className="tabular-nums font-semibold text-green-700 dark:text-green-400">
                    {Mask.formatCurrency(detail.summary.total_wallet_prize)}
                  </span>
                </div>
                <div className="flex justify-between border-b py-1 text-sm">
                  <span>Total pagamento só na Caixa</span>
                  <span className="tabular-nums font-semibold text-blue-700 dark:text-blue-400">
                    {Mask.formatCurrency(detail.summary.total_cashier_prize)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 dark:bg-emerald-900/20">
                  <span className="font-semibold">Prêmio Total (não entra pago Caixa na carteira)</span>
                  <span className="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                    {Mask.formatCurrency(detail.summary.total_prize)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {detail.summary.contests_count} {detail.summary.contests_count === 1 ? "concurso" : "concursos"} -{" "}
                  {detail.summary.products_count} {detail.summary.products_count === 1 ? "bolão" : "bolões"} -{" "}
                  {detail.summary.quotas_count} {detail.summary.quotas_count === 1 ? "cota" : "cotas"}
                </p>
              </CardContent>
            </Card>
          )}

          <div className="text-xs text-muted-foreground">
            <p className="font-semibold">{LEGAL_NOTICE_TITLE}</p>
            <p>{detail.legal_notice}</p>
          </div>
        </>
      )}
    </Main>
  );
};
